import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { ChangeEvent, useContext, useRef, useState } from 'react'
import { CommonContext } from './context/commonContext'
import { InputText } from 'primereact/inputtext'
import { debounce } from 'lodash'
import { PassCheckDialogProps } from '../../types/common'
import {useTranslation} from "react-i18next";
import { tokens } from 'locales/tokens';

const PassChangeDialog = (props: PassCheckDialogProps) => {
    const { $toast, $http } = useContext(CommonContext)
    const { t } = useTranslation();
    const [pass, setPass] = useState<string>('')
    const [newPass, setNewPass] = useState<string>('')
    const [rePass, setRePass] = useState<string>('')
    const [errorMsg, setErrorMsg] = useState<string>('')
    const [reErrorMsg, setReErrorMsg] = useState<string>('')
    const [successMsg, setSuccessMsg] = useState<string>('')
    const [reSuccessMsg, setReSuccessMsg] = useState<string>('')
    const [isDisabled, setIsDisabled] = useState<boolean>(true)
    const [isReDisabled, setIsReDisabled] = useState<boolean>(true)
    const debounceCheckPass = useRef(
        debounce(async (value: string) => {
            setErrorMsg('')
            setSuccessMsg('')

            if (!value) {
                setErrorMsg(props.requiredMessage ? props.requiredMessage : t(tokens.common.password_required))
                setIsDisabled(true)
                return
            }

            const data = await $http().post('/user-if/check-pass', { pass: value })

            if (data) {
                setIsDisabled(false)
                setSuccessMsg(props.successMessage ? props.successMessage : t(tokens.common.password_match_2))
            } else {
                setErrorMsg(props.failMessage ? props.failMessage : t(tokens.common.password_mismatch_2))
                setIsDisabled(true)
            }
        }, 300),
    ).current
    const onHide = () => {
        props.onHide && props.onHide(false)
    }

    const onClickChangePass = async () => {
        const result = await $http().put('/user-if/pass', { pass: newPass })

        if (result) {
            $toast({
                title: t(tokens.common.success),
                text: t(tokens.common.password_change_completed),
                icon: 'success',
            })

            onHide()
        }
    }

    const footer = (
        <div className="grid">
            <div className="col-6">
                <Button
                    label={props.cancelButtonMessage ? props.cancelButtonMessage : t(tokens.common.cancellation)}
                    className="p-button-secondary w-full"
                    type="button"
                    onClick={onHide}
                />
            </div>
            <div className="col-6">
                <Button
                    label={props.checkButtonMessage ? props.checkButtonMessage : t(tokens.common.password_change)}
                    className={`${props.checkButtonClass ? props.checkButtonClass : 'p-button-danger'} w-full`}
                    disabled={isDisabled || isReDisabled}
                    onClick={onClickChangePass}
                />
            </div>
        </div>
    )

    const header = <div>{props.header ? props.header : t(tokens.common.password_change)}</div>

    const onChangePass = (e: ChangeEvent<HTMLInputElement>) => {
        setIsDisabled(true)
        setPass(e.target.value)
        debounceCheckPass(e.target.value)
    }

    const onChangeRePass = (e: ChangeEvent<HTMLInputElement>) => {
        const p = e.currentTarget.value
        setRePass(p)

        if (newPass && p && newPass === p) {
            if (newPass === pass) {
                setReSuccessMsg('')
                setReErrorMsg(t(tokens.common.current_password_matches))
                setIsReDisabled(true)
            } else {
                setReSuccessMsg(t(tokens.common.password_match_2))
                setReErrorMsg('')
                setIsReDisabled(false)
            }
        } else {
            setReSuccessMsg('')
            setReErrorMsg(t(tokens.common.password_mismatch_2))
            setIsReDisabled(true)
        }
    }

    return (
        <Dialog
            header={header}
            footer={footer}
            breakpoints={{ '960px': '25vw', '640px': '50vw' }}
            visible={props.visible}
            contentClassName="pb-2"
            style={{ width: '30vw' }}
            modal
            onHide={onHide}
            baseZIndex={100}
            closable={false}
        >
            <div className="grid">
                <div className="col-12">
                    <div className="grid mb-0">
                        <div className="col-12">
                            <div className="text-600">{props.helpMessage ? props.helpMessage : t(tokens.common.please_enter_password)}</div>
                            <span className="p-input-icon-left mt-4 w-full">
                                <i className="pi pi-lock" />
                                <InputText
                                    type="password"
                                    className="p-inputtext-sm w-full"
                                    placeholder={props.placeHolder ? props.placeHolder : t(tokens.common.password_2)}
                                    value={pass}
                                    onChange={onChangePass}
                                    autoComplete="one-time-code"
                                />
                            </span>
                        </div>
                        <div className="col-12" style={{ minHeight: '15px' }}>
                            {errorMsg ? (
                                <span className="text-red-600 mt-1">{errorMsg}</span>
                            ) : successMsg ? (
                                <span className="text-green-600 mt-1">{successMsg}</span>
                            ) : null}
                        </div>
                        <div className="col-12">
                            <div className="text-600">{props.helpMessage ? props.helpMessage : t(tokens.common.enter_new_password)}</div>
                            <span className="p-input-icon-left mt-2 w-full">
                                <i className="pi pi-lock" />
                                <InputText
                                    type="password"
                                    className="p-inputtext-sm w-full"
                                    placeholder={t(tokens.common.new_password)}
                                    value={newPass}
                                    onChange={(e) => setNewPass(e.currentTarget.value)}
                                    autoComplete="one-time-code"
                                />
                            </span>
                        </div>
                        <div className="col-12">
                            <div className="text-600">{props.helpMessage ? props.helpMessage : t(tokens.common.reenter_new_password)}</div>
                            <span className="p-input-icon-left mt-2 w-full">
                                <i className="pi pi-lock" />
                                <InputText
                                    type="password"
                                    className="p-inputtext-sm w-full"
                                    placeholder={t(tokens.common.re_enter_password)}
                                    value={rePass}
                                    onChange={onChangeRePass}
                                    autoComplete="one-time-code"
                                />
                            </span>
                        </div>
                        <div className="col-12" style={{ minHeight: '15px' }}>
                            {reErrorMsg ? (
                                <span className="text-red-600 mt-1">{reErrorMsg}</span>
                            ) : reSuccessMsg ? (
                                <span className="text-green-600 mt-1">{reSuccessMsg}</span>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default PassChangeDialog
